import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink } from "react-router-dom";
import { ArrowRight, ExternalLink, Menu } from "lucide-react";

export const MOBILE_NAV_ITEMS = [
  ["Home", "/", "home"],
  ["Free Vault", "/library-preview", "free-preview"],
  ["Stag Vault Complete — £25", "/pricing", "full-vault"],
  ["Shop £5 Products", "/store", "shop"],
  ["Build Your Own Vault", "/build-your-own-vault", "byov"],
  ["Earn With Stag Vault", "/affiliates", "earn"],
  ["About", "/about", "about"],
  ["Guides", "/guides", "guides"],
  ["Contact Stag Vault", "/contact", "contact"],
];

const ITEM = "sv-nav-item flex min-h-12 w-full items-center justify-between gap-3 px-1 text-base";
const HISTORY_KEY = "stagvault-mobile-menu";
const CLOSE_BTN = {
  display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  width: 44, height: 44, minWidth: 44, minHeight: 44, padding: 0, margin: 0,
  background: "#050308", border: "1px solid #a7adb9", borderRadius: 0, cursor: "pointer",
  color: "#ffffff", WebkitTextFillColor: "#ffffff", colorScheme: "dark", WebkitAppearance: "none", appearance: "none",
  WebkitTapHighlightColor: "rgba(168, 85, 247, 0.25)", touchAction: "manipulation",
};

/* Dependency-free drawer: plain React state, portal to <body>, inline critical styles (no animation library, no dvh/min()). */
export const MobileNavMenu = ({ user, onAccount, cta, visibility = "md:hidden", testPrefix = "mobile" }) => {
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const pushedHistory = useRef(false);

  const close = useCallback(() => {
    setOpen(false);
    if (pushedHistory.current && typeof window !== "undefined" && window.history.state && window.history.state[HISTORY_KEY]) {
      pushedHistory.current = false;
      window.history.back();
    }
  }, []);

  // Used by navigation links: closing must not call history.back(), which would cancel the link's own navigation.
  const closeForNav = useCallback(() => { pushedHistory.current = false; setOpen(false); }, []);

  const toggle = (e) => {
    e.preventDefault();
    setOpen((v) => !v);
  };

  useEffect(() => {
    if (!open) return undefined;
    const { body } = document;
    const prevOverflow = body.style.overflow;
    body.style.overflow = "hidden";
    try {
      window.history.pushState({ ...(window.history.state || {}), [HISTORY_KEY]: true }, "");
      pushedHistory.current = true;
    } catch { pushedHistory.current = false; }
    const onPop = () => { pushedHistory.current = false; setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("popstate", onPop);
    window.addEventListener("keydown", onKey);
    panelRef.current?.focus({ preventScroll: true });
    return () => {
      body.style.overflow = prevOverflow;
      window.removeEventListener("popstate", onPop);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  const drawer = open && typeof document !== "undefined" ? createPortal(
    <div className="sv-mobile-drawer" style={{ position: "fixed", top: 0, right: 0, bottom: 0, left: 0, zIndex: 2147483000, colorScheme: "dark", forcedColorAdjust: "none" }} data-testid={`${testPrefix}-menu-root`}>
      <button type="button" aria-label="Close menu" onClick={close} style={{ position: "fixed", top: 0, right: 0, bottom: 0, left: 0, width: "100%", height: "100%", zIndex: 2147483001, background: "rgba(0,0,0,0.72)", border: 0, padding: 0, margin: 0, cursor: "pointer", WebkitAppearance: "none", appearance: "none" }} data-testid={`${testPrefix}-menu-backdrop`} />
      <div ref={panelRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label="Site menu" style={{ position: "fixed", top: 0, right: 0, bottom: 0, left: "auto", zIndex: 2147483002, width: "88vw", maxWidth: "22rem", transform: "none", WebkitTransform: "none", visibility: "visible", opacity: 1, pointerEvents: "auto", background: "#050308", color: "#ffffff", WebkitTextFillColor: "#ffffff", borderLeft: "1px solid #40454f", padding: "24px", display: "flex", flexDirection: "column", boxShadow: "-12px 0 40px rgba(0,0,0,0.6)", outline: "none" }} data-testid={`${testPrefix}-menu-panel`}>
        <div className="flex items-center justify-between border-b border-zinc-800 pb-5" style={{ flexShrink: 0 }} data-testid={`${testPrefix}-menu-header`}>
          <img src="/stag-vault-lockup-v2.webp" alt="Stag Vault" className="h-10 w-auto" width="701" height="200" />
          <button type="button" onClick={close} aria-label="Close menu" className="sv-menu-close" style={CLOSE_BTN} data-testid={`${testPrefix}-menu-close`}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" style={{ display: "block", flexShrink: 0 }}>
              <path d="M18 6 6 18" /><path d="m6 6 12 12" />
            </svg>
          </button>
        </div>
        <nav className="sv-mobile-nav mt-4 flex flex-col" style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch" }} aria-label="Mobile navigation">
          {MOBILE_NAV_ITEMS.map(([label, to, key, external]) => (user && key === "full-vault" ? ["Open the Vault", "/vault", key, external] : [label, to, key, external])).map(([label, to, key, external]) => external ? (
            <a key={key} href={to} target="_blank" rel="noopener noreferrer" onClick={closeForNav} className={ITEM} aria-label={`${label} (opens in a new tab)`} data-testid={`${testPrefix}-nav-${key}`}>
              {label}<ExternalLink size={15} className="sv-nav-icon" aria-hidden="true" />
            </a>
          ) : (
            <NavLink key={key} to={to} end={to === "/"} onClick={closeForNav} className={ITEM} aria-label={label} data-testid={`${testPrefix}-nav-${key}`}>
              {label}<ArrowRight size={15} className="sv-nav-icon" aria-hidden="true" />
            </NavLink>
          ))}
          {user && onAccount ? (
            <button type="button" onClick={() => { closeForNav(); onAccount(); }} className={ITEM} aria-label="Account settings" data-testid={`${testPrefix}-nav-account`}>
              Account<ArrowRight size={15} className="sv-nav-icon" aria-hidden="true" />
            </button>
          ) : (
            <Link to={user ? "/vault" : "/sign-in"} onClick={closeForNav} className={ITEM} aria-label={user ? "Account" : "Sign in"} data-testid={`${testPrefix}-nav-account`}>
              {user ? "Account" : "Sign In / Account"}<ArrowRight size={15} className="sv-nav-icon" aria-hidden="true" />
            </Link>
          )}
          {cta && (
            <Link to={cta.to} onClick={closeForNav} className="sv-nav-cta mt-5 inline-flex min-h-12 items-center justify-center gap-2 bg-purple-500 px-5 text-sm font-black uppercase tracking-[0.14em] text-white hover:bg-purple-400" aria-label={cta.label} data-testid={`${testPrefix}-nav-cta`}>
              {cta.label}<ArrowRight size={16} aria-hidden="true" />
            </Link>
          )}
        </nav>
      </div>
    </div>,
    document.body,
  ) : null;

  return (
    <>
      <button
        type="button"
        aria-label={open ? "Close menu" : "Open menu"}
        aria-expanded={open}
        aria-controls={`${testPrefix}-menu-panel`}
        onClick={toggle}
        className={`inline-flex h-11 w-11 shrink-0 items-center justify-center border border-zinc-500 bg-black text-white transition-colors hover:border-purple-400 hover:text-purple-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-purple-400 ${visibility}`}
        data-testid={`${testPrefix}-menu-trigger`}
      >
        <Menu size={22} strokeWidth={2.25} aria-hidden="true" />
      </button>
      {drawer}
    </>
  );
};
